import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button } from "antd";
import { BellOutlined } from "@ant-design/icons";
import {
  getNotifications, getNotificationUnreadCount,
  markAllNotificationsRead, markNotificationsRead,
} from "../services/doctorService";
import NotificationPanel from "./NotificationPanel";

const POLL_MS = 30_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [unread,  setUnread]  = useState(0);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const pollRef = useRef();
  const openRef = useRef(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await getNotificationUnreadCount();
      setUnread(res.data?.unread_count ?? 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications();
      setItems(Array.isArray(res.data) ? res.data : (res.data?.results ?? []));
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(() => {
      fetchUnread();
      if (openRef.current) fetchList();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnread, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    const next = !open;
    openRef.current = next;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClose = () => {
    openRef.current = false;
    setOpen(false);
  };

  const handleItemClick = async (item) => {
    handleClose();
    if (!item.is_read) {
      try {
        await markNotificationsRead([item.id]);
        setItems(prev => prev.map(n => n.id === item.id ? { ...n, is_read: true } : n));
        setUnread(c => Math.max(0, c - 1));
      } catch { /* silent */ }
    }
    if (item.link_url) navigate(item.link_url);
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const badgeCount = unread > 99 ? "99+" : unread;

  return (
    <>
      <Badge count={badgeCount} size="small" offset={[-2, 4]} style={{ backgroundColor: "#ff4d4f" }}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          aria-label="Notifications"
          onClick={handleOpen}
        />
      </Badge>

      <NotificationPanel
        visible={open}
        onClose={handleClose}
        notifications={items}
        unreadCount={unread}
        loading={loading}
        onMarkAllRead={handleMarkAll}
        onItemClick={handleItemClick}
      />
    </>
  );
}
